import { CustomToolParameterType } from "./custom.js";
const customFieldValueType = "string | number | boolean | undefined";
function buildStringProperty(name, doc) {
  return { name, type: "string | undefined", kind: "property", doc };
}
function buildNullableStringProperty(name, doc) {
  return { name, type: "string | null", kind: "property", doc };
}
function buildCustomFieldsMember(doc, fields) {
  return {
    name: "custom_fields",
    kind: "property",
    doc,
    members: fields.map((field) => ({
      name: field.field_name,
      type: customFieldValueType,
      kind: "property",
      label: field.display_name,
      doc: ""
    }))
  };
}
function buildParameterType(parameter) {
  switch (parameter.type) {
    case CustomToolParameterType.ARRAY: {
      const itemType = parameter.array_item_type === CustomToolParameterType.OBJECT ? "Record<string, unknown>" : parameter.array_item_type ?? CustomToolParameterType.STRING;
      return `${itemType}[] | undefined`;
    }
    case CustomToolParameterType.OBJECT:
      return "Record<string, unknown> | undefined";
    default:
      return `${parameter.type} | undefined`;
  }
}
const logFunctionType = "(...values: unknown[]) => void";
const codeToolScriptGlobals = [
  {
    name: "fetch",
    type: '(url: string | URL, init?: { method?: "GET" | "HEAD" | "POST" | "PUT" | "PATCH" | "DELETE"; headers?: Record<string, string>; body?: string }) => Promise<{ status: number; ok: boolean; headers: { get(name: string): string | null }; text(): Promise<string>; json(): Promise<unknown> }>',
    kind: "function",
    doc: "Sends a GET, HEAD, POST, PUT, PATCH or DELETE, with a string body, to one of the tool's allowed domains. At most a few per run; the response is read in full."
  },
  {
    name: "URL",
    type: "typeof URL",
    kind: "class",
    doc: "Parses and builds URLs, with `searchParams`."
  },
  {
    name: "URLSearchParams",
    type: "typeof URLSearchParams",
    kind: "class",
    doc: "Reads and builds query strings."
  },
  {
    name: "console",
    kind: "property",
    doc: "Writes to the run's logs.",
    members: ["log", "info", "warn", "error"].map((level) => ({
      name: level,
      type: logFunctionType,
      kind: "function",
      doc: `Logs at the ${level} level.`
    }))
  },
  {
    name: "Date",
    type: "typeof Date",
    kind: "class",
    doc: "JavaScript's own `Date`, its local time the company's zone (`input.company_timezone`, UTC without one): `getHours()`, `toString()`, `new Date(2026, 0, 5)` and the rest read and build wall time there. `toLocaleString`, `toLocaleDateString` and `toLocaleTimeString` write en-US only (no locale or `\"en-US\"`), in the company's zone unless `timeZone` names another, with `dateStyle`/`timeStyle` (full, long, medium, short) or `DateTime#toLocaleString`'s options; others throw. Prefer `DateTime`."
  },
  {
    name: "DateTime",
    type: "typeof DateTime",
    kind: "class",
    doc: 'An instant in a time zone, in years -9999 to 9999: a subset of Luxon\'s `DateTime`, in en-US. A zone left out is the company\'s (`input.company_timezone`), UTC without one; a zone is an IANA name such as `America/Chicago`, `UTC`, or an offset such as `-05:00`. Immutable; an invalid date, unit, zone or option throws a `RangeError`. To write a date or time for people, use `toLocaleString`: with a preset such as `DateTime.DATETIME_MED` (listed below; `DATE_SHORT` by default) or with `Intl.DateTimeFormat`-style options such as `{ weekday: "long", hour: "numeric", minute: "2-digit" }`: `weekday` (short, long), `year` (numeric; 2-digit only with a numeric month, for 1950 to 2049), `month` (numeric, 2-digit, short, long), `day`, `hour`, `minute`, `second` (numeric, 2-digit), `timeZoneName` (short, long; with an hour), `hourCycle` (h11, h12, h23) or `hour12`. The date is a day; a month and day; or a year, month and day, each with or without a weekday; or a weekday alone, a month alone, or a year and month, the last two with no time. The time is an hour; an hour and minute; or an hour, minute and second. A spelled-out month and a weekday have the same width; a 2-digit month, day and hour go together. Any other option, value or combination throws a `RangeError`. `toFormat(pattern)` is for a fixed layout another system expects (such as `yyyy-MM-dd`) when no preset or options give it, not for text people read; its fields are y, yyyy, M, MM, MMM, MMMM, L, LLL, LLLL, d, dd, EEE, EEEE, ccc, cccc, h, hh, H, HH, m, mm, s, ss, a, and text in single quotes, and any other field throws a `RangeError`. For machines: `toISO()` (such as `2026-10-21T14:30:00.000-05:00`, also `toString()` and JSON), `toISODate()`, `toMillis()`. Getters: `year`, `month` (1\u201312), `day`, `hour`, `minute`, `second`, `millisecond`, `weekday` (Monday is 1), `zoneName`, `offset` (minutes). Methods: `setZone(zone)`, `toUTC()`; `plus(units)`, `minus(units)` and `set(units)`, such as `{ days: 1 }`, in whole years, quarters (not in `set`), months, weeks (not in `set`), days, hours, minutes, seconds or milliseconds, calendar units on the zone\'s wall clock; `startOf(unit)`, `endOf(unit)`, `hasSame(other, unit)` for year, month, week (from Monday), day, hour or minute; `diff(other, unit)` (milliseconds by default, or years, months, weeks, days, hours, minutes, seconds), which answers an object such as `{ hours: 1.5 }`, fractional; `equals(other)` (same instant and zone). Compare with `<` and `>`.',
    members: [
      {
        name: "now",
        type: "() => DateTime",
        kind: "function",
        doc: "The run's current time in the company's zone, fixed for the whole run."
      },
      {
        name: "fromISO",
        type: "(text: string, options?: { zone?: string }) => DateTime",
        kind: "function",
        doc: "Parses ISO 8601 text: `YYYY-MM-DD`, optionally followed by `T` or a space and `HH:MM`, `HH:MM:SS` or `HH:MM:SS.fraction` (up to 9 digits), then optionally `Z` or an offset such as `-05:00` or `-0500`; any other text throws a `RangeError`. Text with an offset keeps its instant, shown in `zone`; text without one is wall time in `zone`."
      },
      {
        name: "fromFormat",
        type: "(text: string, pattern: string, options?: { zone?: string }) => DateTime",
        kind: "function",
        doc: "Parses text matching a pattern, as wall time in `zone`. Pattern fields: y, yy, yyyy, M, MM, MMM, MMMM, d, dd, H, HH, h, hh, m, mm, s, ss, a, and text in single quotes; month names and AM/PM are English. Fields left out above the largest one given are today's, below it their start."
      },
      {
        name: "fromMillis",
        type: "(milliseconds: number, options?: { zone?: string }) => DateTime",
        kind: "function",
        doc: "From milliseconds since the Unix epoch."
      },
      {
        name: "fromObject",
        type: "(units: { year?: number; month?: number; day?: number; hour?: number; minute?: number; second?: number; millisecond?: number }, options?: { zone?: string }) => DateTime",
        kind: "function",
        doc: "Wall time in `zone` from its parts. Parts left out above the largest one given are today's, below it their start: `{ hour: 9 }` is 9:00 today."
      },
      {
        name: "DATE_SHORT",
        type: "Readonly<Intl.DateTimeFormatOptions>",
        kind: "property",
        doc: "A `toLocaleString` preset, Luxon's: `10/21/2026`."
      },
      {
        name: "DATE_MED",
        type: "Readonly<Intl.DateTimeFormatOptions>",
        kind: "property",
        doc: "A `toLocaleString` preset, Luxon's: `Oct 21, 2026`."
      },
      {
        name: "DATE_MED_WITH_WEEKDAY",
        type: "Readonly<Intl.DateTimeFormatOptions>",
        kind: "property",
        doc: "A `toLocaleString` preset, Luxon's: `Wed, Oct 21, 2026`."
      },
      {
        name: "DATE_FULL",
        type: "Readonly<Intl.DateTimeFormatOptions>",
        kind: "property",
        doc: "A `toLocaleString` preset, Luxon's: `October 21, 2026`."
      },
      {
        name: "DATE_HUGE",
        type: "Readonly<Intl.DateTimeFormatOptions>",
        kind: "property",
        doc: "A `toLocaleString` preset, Luxon's: `Wednesday, October 21, 2026`."
      },
      {
        name: "TIME_SIMPLE",
        type: "Readonly<Intl.DateTimeFormatOptions>",
        kind: "property",
        doc: "A `toLocaleString` preset, Luxon's: `2:30 PM`."
      },
      {
        name: "TIME_WITH_SECONDS",
        type: "Readonly<Intl.DateTimeFormatOptions>",
        kind: "property",
        doc: "A `toLocaleString` preset, Luxon's: `2:30:00 PM`."
      },
      {
        name: "TIME_WITH_SHORT_OFFSET",
        type: "Readonly<Intl.DateTimeFormatOptions>",
        kind: "property",
        doc: "A `toLocaleString` preset, Luxon's: `2:30:00 PM CDT`."
      },
      {
        name: "TIME_WITH_LONG_OFFSET",
        type: "Readonly<Intl.DateTimeFormatOptions>",
        kind: "property",
        doc: "A `toLocaleString` preset, Luxon's: `2:30:00 PM Central Daylight Time`."
      },
      {
        name: "TIME_24_SIMPLE",
        type: "Readonly<Intl.DateTimeFormatOptions>",
        kind: "property",
        doc: "A `toLocaleString` preset, Luxon's: `14:30`."
      },
      {
        name: "TIME_24_WITH_SECONDS",
        type: "Readonly<Intl.DateTimeFormatOptions>",
        kind: "property",
        doc: "A `toLocaleString` preset, Luxon's: `14:30:00`."
      },
      {
        name: "TIME_24_WITH_SHORT_OFFSET",
        type: "Readonly<Intl.DateTimeFormatOptions>",
        kind: "property",
        doc: "A `toLocaleString` preset, Luxon's: `14:30:00 CDT`."
      },
      {
        name: "TIME_24_WITH_LONG_OFFSET",
        type: "Readonly<Intl.DateTimeFormatOptions>",
        kind: "property",
        doc: "A `toLocaleString` preset, Luxon's: `14:30:00 Central Daylight Time`."
      },
      {
        name: "DATETIME_SHORT",
        type: "Readonly<Intl.DateTimeFormatOptions>",
        kind: "property",
        doc: "A `toLocaleString` preset, Luxon's: `10/21/2026, 2:30 PM`."
      },
      {
        name: "DATETIME_SHORT_WITH_SECONDS",
        type: "Readonly<Intl.DateTimeFormatOptions>",
        kind: "property",
        doc: "A `toLocaleString` preset, Luxon's: `10/21/2026, 2:30:00 PM`."
      },
      {
        name: "DATETIME_MED",
        type: "Readonly<Intl.DateTimeFormatOptions>",
        kind: "property",
        doc: "A `toLocaleString` preset, Luxon's: `Oct 21, 2026, 2:30 PM`."
      },
      {
        name: "DATETIME_MED_WITH_SECONDS",
        type: "Readonly<Intl.DateTimeFormatOptions>",
        kind: "property",
        doc: "A `toLocaleString` preset, Luxon's: `Oct 21, 2026, 2:30:00 PM`."
      },
      {
        name: "DATETIME_MED_WITH_WEEKDAY",
        type: "Readonly<Intl.DateTimeFormatOptions>",
        kind: "property",
        doc: "A `toLocaleString` preset, Luxon's: `Wed, Oct 21, 2026, 2:30 PM`."
      },
      {
        name: "DATETIME_FULL",
        type: "Readonly<Intl.DateTimeFormatOptions>",
        kind: "property",
        doc: "A `toLocaleString` preset, Luxon's: `October 21, 2026 at 2:30 PM CDT`."
      },
      {
        name: "DATETIME_FULL_WITH_SECONDS",
        type: "Readonly<Intl.DateTimeFormatOptions>",
        kind: "property",
        doc: "A `toLocaleString` preset, Luxon's: `October 21, 2026 at 2:30:00 PM CDT`."
      },
      {
        name: "DATETIME_HUGE",
        type: "Readonly<Intl.DateTimeFormatOptions>",
        kind: "property",
        doc: "A `toLocaleString` preset, Luxon's: `Wednesday, October 21, 2026 at 2:30 PM Central Daylight Time`."
      },
      {
        name: "DATETIME_HUGE_WITH_SECONDS",
        type: "Readonly<Intl.DateTimeFormatOptions>",
        kind: "property",
        doc: "A `toLocaleString` preset, Luxon's: `Wednesday, October 21, 2026 at 2:30:00 PM Central Daylight Time`."
      }
    ]
  }
];
function buildCodeToolScriptApi(options) {
  const input = {
    name: "input",
    kind: "property",
    doc: "The call this tool runs on, and the parameters the agent supplied.",
    members: [
      {
        name: "params",
        kind: "property",
        doc: "The tool's parameters as the agent supplied them; absent when left out.",
        members: options.parameters.map((parameter) => ({
          name: parameter.name,
          type: buildParameterType(parameter),
          kind: "property",
          doc: parameter.description ?? ""
        }))
      },
      {
        name: "contact",
        kind: "property",
        doc: "The caller as the contacts API returns them, a field `null` when unset.",
        members: [
          {
            name: "id",
            type: "string",
            kind: "property",
            doc: "The contact's id; a placeholder in a simulation."
          },
          buildNullableStringProperty("email", "Email address."),
          buildNullableStringProperty("phone_number", "Phone number."),
          buildNullableStringProperty("ext_id", "Your own id for the contact."),
          buildNullableStringProperty("first_name", "First name."),
          buildNullableStringProperty("last_name", "Last name."),
          buildCustomFieldsMember(
            "The contact's custom field values, by key.",
            options.contactCustomFields
          )
        ]
      },
      {
        name: "customer_interaction",
        kind: "property",
        doc: "The customer interaction this call is recorded as.",
        members: [
          {
            name: "id",
            type: "string",
            kind: "property",
            doc: "The customer interaction's id; a placeholder in a simulation."
          },
          buildCustomFieldsMember(
            "The interaction's custom field values, by key.",
            options.interactionCustomFields
          )
        ]
      },
      {
        name: "call",
        kind: "property",
        doc: "The phone call; absent on a call-task turn, which has no call in progress.",
        members: [
          {
            name: "direction",
            type: '"inbound" | "outbound"',
            kind: "property",
            doc: "Whether the call came in or went out."
          },
          buildNullableStringProperty(
            "user_phone_number",
            "The other party's number, whatever the direction; null when withheld or unknown."
          ),
          buildStringProperty(
            "ext_id",
            "Your own id for the call, when you gave one."
          ),
          buildStringProperty(
            "forwarded_from",
            "The number that forwarded this inbound call to the agent (e.g. your business line), in E.164; absent when the call wasn't forwarded or the carrier didn't pass the number on."
          )
        ]
      },
      {
        name: "call_task",
        kind: "property",
        doc: "The call task this run belongs to: on a task's turn, and on a call that belongs to a task.",
        members: [
          {
            name: "id",
            type: "string",
            kind: "property",
            doc: "The call task's id; a placeholder in a simulation."
          },
          buildStringProperty(
            "ext_id",
            "Your own id for the task, when it has one."
          )
        ]
      },
      {
        name: "company_timezone",
        type: "string",
        kind: "property",
        doc: "The company's default time zone, such as `America/New_York`: `DateTime`'s default zone and `Date`'s local time."
      }
    ]
  };
  const scriptContext = {
    name: "context",
    kind: "property",
    doc: "What the tool brings to the run.",
    members: [
      {
        name: "secrets",
        kind: "property",
        doc: "The tool's secrets, by name. Each is sent only to its own domains.",
        members: options.secretNames.map((name) => ({
          name,
          type: "string",
          kind: "property",
          doc: "A secret: usable in a request to its domains, and redacted from logs and results."
        }))
      },
      {
        name: "endCall",
        type: "() => ToolOutput",
        kind: "function",
        doc: "Return its result to end the call once the agent has finished saying what it was saying: `return context.endCall()`. Nothing is said after this tool, so it suits a path where no reply is needed. Calling it without returning its result has no effect. It throws when there is no call to end, such as on a call task's turn; catch it to carry on."
      }
    ]
  };
  return {
    parameters: [input, scriptContext],
    globals: codeToolScriptGlobals
  };
}
export {
  buildCodeToolScriptApi
};
//# sourceMappingURL=codeToolScriptApi.js.map
